import { useEffect, useState } from 'react';
import { FiCheck, FiImage, FiSave } from 'react-icons/fi';
import { usePortfolioAdmin } from '../_Hooks/usePortfolioAdmin';
import { RESOLVE_PORTFOLIO_MEDIA_URL } from '../../../_Shared/resolveMediaUrl';

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

const toFormState = (data, fields) => Object.fromEntries(fields.map((field) => {
    const value = data?.[field.name];
    if (field.type === 'tags') return [field.name, Array.isArray(value) ? value.join(', ') : (value || '')];
    if (field.type === 'checkbox') return [field.name, value === true];
    return [field.name, value ?? ''];
}));

const revokePreview = (value) => {
    if (value && String(value).startsWith('blob:')) URL.revokeObjectURL(value);
};

export const AdminSingletonEditor = ({ title, description, getEndpoint, saveEndpoint, fields = [], onSaved = null }) => {
    const api = usePortfolioAdmin();
    const [values, setValues] = useState(() => toFormState({}, fields));
    const [fileValues, setFileValues] = useState({});
    const [filePreviews, setFilePreviews] = useState({});
    const [imageErrors, setImageErrors] = useState({});
    const [fileInputVersion, setFileInputVersion] = useState(0);
    const [saved, setSaved] = useState(false);
    const [loadError, setLoadError] = useState('');


    useEffect(() => {
        let active = true;
        api.get(getEndpoint).then((data) => {
            if (!active) return;
            setValues(toFormState(data || {}, fields));
            setImageErrors({});
        }).catch((err) => active && setLoadError(err.message));
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getEndpoint]);

    useEffect(() => () => {
        Object.values(filePreviews).forEach(revokePreview);
    }, [filePreviews]);

    const change = (field, value) => {
        setSaved(false);
        setValues((prev) => ({ ...prev, [field]: value }));
    };

    const selectImage = (fieldName, file) => {
        setSaved(false);
        setLoadError('');
        setImageErrors((prev) => ({ ...prev, [fieldName]: false }));

        if (!file) {
            setFileValues((prev) => ({ ...prev, [fieldName]: null }));
            setFilePreviews((prev) => {
                revokePreview(prev[fieldName]);
                const next = { ...prev };
                delete next[fieldName];
                return next;
            });
            return;
        }

        if (!String(file.type || '').startsWith('image/')) {
            setLoadError('El archivo seleccionado no es una imagen valida.');
            return;
        }

        if (file.size > MAX_IMAGE_BYTES) {
            setLoadError('La imagen supera el limite de 8 MB.');
            return;
        }

        const preview = URL.createObjectURL(file);
        setFileValues((prev) => ({ ...prev, [fieldName]: file }));
        setFilePreviews((prev) => {
            revokePreview(prev[fieldName]);
            return { ...prev, [fieldName]: preview };
        });
    };

    const clearPendingImages = () => {
        Object.values(filePreviews).forEach(revokePreview);
        setFilePreviews({});
        setFileValues({});
        setImageErrors({});
        setFileInputVersion((value) => value + 1);
    };

    const submit = async (event) => {
        event.preventDefault();
        const body = new FormData();
        fields.forEach((field) => {
            if (field.type === 'image') {
                if (fileValues[field.name]) body.append(field.name, fileValues[field.name], fileValues[field.name].name);
                return;
            }
            const value = field.type === 'checkbox' ? (values[field.name] ? '1' : '0') : values[field.name];
            body.append(field.name, value ?? '');
        });

        try {
            const data = await api.securePost(saveEndpoint, body);
            setValues(toFormState(data || values, fields));
            clearPendingImages();
            setSaved(true);
            setLoadError('');
            onSaved?.(data);
        } catch (err) {
            setLoadError(err.message);
        }
    };

    return (
        <section className="PBADMIN__panel">
            <div className="PBADMIN__panel_header"><div><h2>{title}</h2>{description && <p>{description}</p>}</div>{saved && <span className="PBADMIN__saved"><FiCheck /> Guardado</span>}</div>
            {loadError && <div className="PBADMIN__alert">{loadError}</div>}
            <form className="PBADMIN__form_grid" onSubmit={submit}>
                {fields.map((field) => {
                    if (field.type === 'image') {
                        const current = values[field.name];
                        const selected = fileValues[field.name];
                        const source = filePreviews[field.name] || RESOLVE_PORTFOLIO_MEDIA_URL(current);
                        const canRender = !!source && !imageErrors[field.name];

                        return (
                            <label key={field.name} className={`PBADMIN__field PBADMIN__field--${field.span || 2}`}>
                                <span>{field.label}</span>
                                <div className="PBADMIN__image_field">
                                    <div className="PBADMIN__image_preview">
                                        {canRender ? (
                                            <img
                                                src={source}
                                                alt={selected ? 'Vista previa' : 'Actual'}
                                                onLoad={() => setImageErrors((prev) => ({ ...prev, [field.name]: false }))}
                                                onError={() => setImageErrors((prev) => ({ ...prev, [field.name]: true }))}
                                            />
                                        ) : <FiImage />}
                                    </div>
                                    <div className="PBADMIN__image_picker">
                                        <input
                                            key={`${field.name}-${fileInputVersion}`}
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                                            onChange={(event) => selectImage(field.name, event.target.files?.[0] || null)}
                                        />
                                        {selected && <small>Seleccionada: {selected.name} · {(selected.size / 1024 / 1024).toFixed(2)} MB</small>}
                                        {!selected && current && imageErrors[field.name] && <small className="PBADMIN__image_warning">La imagen guardada no existe o no es accesible. Selecciona una nueva para reemplazarla.</small>}
                                    </div>
                                </div>
                            </label>
                        );
                    }
                    if (field.type === 'textarea') return <label key={field.name} className={`PBADMIN__field PBADMIN__field--${field.span || 2}`}><span>{field.label}</span><textarea rows={field.rows || 4} value={values[field.name]} onChange={(e) => change(field.name, e.target.value)} placeholder={field.placeholder || ''} /></label>;
                    if (field.type === 'checkbox') return <label key={field.name} className="PBADMIN__check"><input type="checkbox" checked={!!values[field.name]} onChange={(e) => change(field.name, e.target.checked)} /><span>{field.label}</span></label>;
                    return <label key={field.name} className={`PBADMIN__field PBADMIN__field--${field.span || 1}`}><span>{field.label}</span><input type={field.type || 'text'} value={values[field.name]} onChange={(e) => change(field.name, e.target.value)} placeholder={field.placeholder || ''} /></label>;
                })}
                <div className="PBADMIN__form_footer"><button className="PBADMIN__primary" type="submit" disabled={api.isLoading}><FiSave /> {api.isLoading ? 'Guardando...' : 'Guardar cambios'}</button></div>
            </form>
        </section>
    );
};
